package com.swetdot.swetgram.ui

import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.Sizes

/**
 * Which mark the bottom bar draws for each tab.
 *
 * THE GEOMETRY IS GONE FROM THIS FILE. These four were drawn here with `drawCircle`, `drawArc` and
 * `drawRoundRect` at fractions of the passed size, because Compose Multiplatform ships no icon set
 * and pasting SVG path data from memory renders as a smear. `SwetIcons` is that set now -- authored
 * on one 24-unit grid at one stroke width, and proofed by rasterising the very path strings it
 * ships -- so the four tabs name marks from it instead of describing their own.
 *
 * The three defects the hand-drawn versions carried, and which the shared set does not have:
 *
 *  - The Chats bubble was the only FILLED mark of the four, and its body and tail were two subpaths
 *    that had to wind the same way or the union bit a wedge out of the bubble where the tail joined.
 *  - The Groups mark had two shoulder arcs crossing with no knockout, and two heads at different
 *    radii, so it read as a tangle rather than as two people.
 *  - The Profile mark's head and shoulders were separated by an eighth of a pixel and fused into a
 *    blob at any size a phone actually draws.
 *
 * ALL FOUR ARE STROKE MARKS AT THE SAME WEIGHT, and losing the odd fill loses no information.
 * `SwetBottomNav` animates one `tint` between `text.primary` and `text.secondary` and hands that
 * same colour to the icon AND the label -- so the active tab is a COLOUR on both halves of the item.
 * A filled-versus-outlined bubble was a second encoding of that same fact, on one tab out of four,
 * and it made the Chats silhouette differ from its neighbours' in every state rather than in the
 * selected one. Nothing here reads `selectedIndex`; the tint is the whole of the state.
 *
 * The tab is announced as selected by `SwetBottomNav`'s own `semantics { selected }`, and the label
 * sits directly under the mark, so [contentDescription][SwetIcon] is null: a screen reader that
 * announced the icon too would say the tab's name twice.
 */
enum class TabGlyphKind { Chats, Discover, Picso, Tasks, Groups, Profile }

@Composable
fun TabGlyph(kind: TabGlyphKind, tint: Color, modifier: Modifier = Modifier) {
    val icon = when (kind) {
        TabGlyphKind.Chats -> SwetIcons.ChatNavigation
        TabGlyphKind.Discover -> SwetIcons.Search
        TabGlyphKind.Picso -> SwetIcons.ImageIcon
        TabGlyphKind.Tasks -> SwetIcons.TasksNavigation
        TabGlyphKind.Groups -> SwetIcons.GroupsNavigation
        TabGlyphKind.Profile -> SwetIcons.ProfileNavigation
    }

    SwetIcon(
        icon = icon,
        tint = tint,
        contentDescription = null,
        modifier = modifier,
        // §9.4's `icon.md`, which is the 24dp the Canvas hard-coded. Stated rather than left to
        // `SwetIcon`'s default so the bottom bar's 64dp arithmetic -- 8dp padding, glyph, 4dp gap,
        // 12sp label -- still has its glyph term written down somewhere.
        size = Sizes.iconMedium,
    )
}
